import {Component, forwardRef, Input, OnInit} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import {IFileUpload} from '../../models/file-upload.model';
import {BlobClientService} from '../../services/blob.service';
import {ToastrService} from 'ngx-toastr';

const noop = () => {
};

const MAX_FILE_SIZE_BYTES = 28_000_000;
const ACCEPTED_EXTENSIONS = ['.docx', '.pdf', '.bmp', '.doc', '.jpg', '.jpeg', '.png', '.gif'];

@Component({
    selector: 'eil-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true
        }
    ],
    standalone: false
})
export class FileUploadComponent implements OnInit, ControlValueAccessor, Validator {
    @Input() public disabled = false;
    @Input() public applicationId: string;
    @Input() public downloadType: 'receipt' | 'eventFile' = 'eventFile';

    files: IFileUpload[] = [];
    validationErrors: ValidationErrors | null = null;

    // Callbacks initialized to noop to prevent errors before the ControlValueAccessor or Validator
    // framework sets the actual implementations. Angular's forms API requires these callbacks to be
    // present at all times, so we provide no-op implementations initially.
    private onChangeCallback: (_: IFileUpload[]) => void = noop;
    private onTouchedCallback: () => void = noop;
    private onValidatorChange: () => void = noop;

    constructor(private blobClient: BlobClientService, private toastrService: ToastrService) {
    }

    get value(): IFileUpload[] {
        return this.files;
    }

    // --- ControlValueAccessor ---

    set value(fileToUpload: IFileUpload[]) {
        const next = fileToUpload ?? [];
        if (next !== this.files) {
            this.files = next;
            this.onChangeCallback(this.files);
            this.onValidatorChange();
        }
    }

    ngOnInit(): void {
    }

    writeValue(value: IFileUpload[]): void {
        const next = value ?? [];
        if (next !== this.files) {
            this.files = next;
            // Notify Angular so validation is re-run for the new value.
            this.onValidatorChange();
        }
    }

    registerOnChange(fn: (_: IFileUpload[]) => void): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // --- Validator ---

    /**
     * Validates all files in the current set. Runs during Angular's validation cycle.
     * Only validates newly selected files, not already-uploaded files (which were validated on upload).
     * Key by slot index, not filename, so duplicate filenames don't collide.
     */
    validate(_control: AbstractControl): ValidationErrors | null {
        const invalidFiles: { [slotIndex: number]: string } = {};
        for (let i = 0; i < this.files.length; i++) {
            const file = this.files[i];
            // Skip empty slots (no file chosen yet).
            if (!file.filename) {
                continue;
            }
            // Skip already-uploaded files (they were validated on upload)
            if (file.isUploaded) {
                continue;
            }

            const error = this.validateFile(file);
            if (error) {
                invalidFiles[i] = error;
            }
        }
        this.validationErrors = Object.keys(invalidFiles).length > 0 ? {invalidFiles} : null;
        return this.validationErrors;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    // --- Public methods ---

    addFile(): void {
        this.files.splice(0, 0, {});
        this.onTouchedCallback();
        this.onChangeCallback(this.files);
    }

    fileChanged(file: IFileUpload, e: Event): void {
        const input = e.target as HTMLInputElement;
        const selectedFile = input.files?.[0];
        if (!selectedFile) {
            return;
        }

        this.onTouchedCallback();
        file.filename = selectedFile.name;
        file.size = selectedFile.size;

        const validationError = this.validateFile(file);
        if (validationError) {
            this.onChangeCallback(this.files);
            return;
        }

        file.isSelected = true;
        file.isUploaded = false;
        file.isUploading = true;
        this.onChangeCallback(this.files);

        this.blobClient.uploadFile(selectedFile, (isSuccess: boolean, result: string, _error: any) => {
            if (isSuccess) {
                file.isUploading = false;
                file.isUploaded = true;
                file.blobStorageUrl = result;
                this.onChangeCallback(this.files);
            } else {
                this.toastrService.error('Something went wrong, please try again later.');
                this.removeFile(file);
            }
        });
    }

    fileIsAcceptable(filename: string): boolean {
        if (!filename) {
            return false;
        }
        const lower = filename.toLowerCase();
        return ACCEPTED_EXTENSIONS.some(ext => lower.endsWith(ext));
    }

    isImageLink(linkText: string): boolean {
        return /jpe?g|gif|png$/.test(linkText);
    }

    // Reference: https://stackoverflow.com/questions/35038884/download-file-from-bytes-in-javascript
    downloadFile(file: IFileUpload): void {
        const download = this.downloadType === 'eventFile'
            ? this.blobClient.downloadEventFile.bind(this.blobClient)
            : this.blobClient.downloadReceipt.bind(this.blobClient);

        download(this.applicationId, file.blobStorageUrl, file.filename, (success: boolean, resultBlob: any) => {
            if (success) {
                const blob = new Blob([resultBlob], {type: 'application/octet-stream'});
                const htmlLink = document.createElement('a');
                htmlLink.href = window.URL.createObjectURL(blob);
                htmlLink.download = file.filename;
                htmlLink.click();
                window.URL.revokeObjectURL(htmlLink.href);
                htmlLink.remove();
            }
        });
    }

    deleteFile(file: IFileUpload): void {
        this.onTouchedCallback();
        if (!confirm(`Are you sure you want to delete ${file.filename}?`)) {
            return;
        }

        file.isDeleting = true;
        if (!file.blobStorageUrl) {
            this.removeFile(file);
            return;
        }

        const parts = file.blobStorageUrl.split('/');
        const blobName = parts[parts.length - 1];
        this.blobClient.deleteFile(blobName)
            .then(() => {
                this.removeFile(file);
            })
            .catch((reason: any) => {
                this.toastrService.error('Something went wrong, please try again later.');
                console.error(reason);
                file.isDeleting = false;
            });
    }

    cancelFile(file: IFileUpload): void {
        const index = this.files.indexOf(file);
        if (index > -1) {
            this.files.splice(index, 1);
            this.onTouchedCallback();
            this.onChangeCallback(this.files);
            this.onValidatorChange();
        }
    }

    private validateFile(file: IFileUpload): string | null {
        if (file.isUploading) {
            return `The file "${file.filename}" is still in the process of uploading`;
        }
        if (!(file.size < MAX_FILE_SIZE_BYTES)) {
            return `The file "${file.filename}" exceeds the maximum allowed size of 28 MB. Please choose a smaller file and try again.`;
        }
        if (!this.fileIsAcceptable(file.filename)) {
            return `The file "${file.filename}" is not an accepted file type. Only word documents, pictures, or PDFs are allowed (.pdf, .bmp, .docx, .doc, .jpg, .jpeg, .png, .gif).`;
        }
        return null;
    }

    private removeFile(file: IFileUpload): void {
        const index = this.files.indexOf(file);
        if (index > -1) {
            this.files.splice(index, 1);
            this.onChangeCallback(this.files);
            this.onValidatorChange();
        }
    }
}
